/* Bilingual strings for the business + vendor dashboards. Complete coverage
 * of every customer-facing label. Falls back to English if a key is missing
 * in the active locale. */
import { useEffect, useCallback } from "react";
import { api } from "./api";
import { useI18n } from "./i18n";

export const DASHBOARD_STRINGS = {
  en: {
    /* ---- Tabs ---- */
    tab_overview: "Overview",
    tab_customers: "Customers",
    tab_analytics: "Analytics",
    tab_profile: "Profile",
    tab_my_business: "My Business",
    dashboard_label: "DASHBOARD",
    dashboard_business: "Business",
    dashboard_user: "User",
    back_to_home: "Back to Home",
    edit: "Edit",
    tab_jobs: "Jobs",
    tab_messages: "Messages",
    jobs_heading: "Jobs",
    messages_heading: "Messages",
    id_locked_label: "ID Photo",

    /* ---- Overview ---- */
    welcome_back: "Welcome back",
    eyebrow_this_week: "This week",
    todays_checkins: "Today's check-ins",
    todays_interactions: "Today's interactions",
    followers: "Followers",
    saves: "Saves",
    qr_scans: "QR scans",
    status: "Status",
    status_active: "Active",
    status_inactive: "Inactive",
    your_qr: "Your QR",
    your_qr_open: "Open",
    your_qr_sub: "Download & share",
    saves_this_week: "Saves this week",
    checkins_this_week: "Check-ins this week",
    interactions_this_week: "Interactions this week",
    rev_section: "Revenue",
    rev_week: "Estimated revenue this week",
    rev_week_vendor: "Estimated interaction revenue this week",
    rev_lifetime: "Lifetime estimated revenue",
    rev_disclaimer: "Estimated by customers — not verified transaction data",
    rev_top_spender: "Top spender this week",
    visit_singular: "visit",
    visit_plural: "visits",
    interaction_singular: "interaction",
    interaction_plural: "interactions",
    spend_estimated_note: "Estimated by customer — not verified transaction data",
    no_spend_recorded: "No spend recorded",
    tonights_controls: "Tonight's controls",
    open_now: "Open now",
    open_now_hint: "Show to travelers as currently open. Toggle off the moment you lock the doors.",
    here_tonight: "Here Tonight",
    here_tonight_hint: "Surface this place in the Tonight rail of the Home screen.",
    vip_available: "VIP available",
    vip_available_hint: "Indicates a VIP table / lounge is available for booking.",
    open_book: "Open the full Customer Book.",
    qr_show_door: "Show this at the door to log check-ins.",
    profile_card_hint: "Hours, photos, contact, social links.",
    quick_actions: "Quick actions",
    send_promotion: "Send a promotion",
    send_promotion_hint: "Reach customers who saved or interacted with you.",
    view_customer_book: "View Customer Book",
    view_customer_book_hint: "Starred customers, private notes, interaction history.",
    recent_interactions_label: "Recent interactions",
    recent_interactions_empty: "No interactions yet — your QR + WhatsApp taps will appear here.",
    reactivate_listing: "Reactivate your listing",
    reactivate_profile: "Reactivate your profile",
    listing_paused_user: "This listing may no longer be active — contact them directly to confirm",

    /* ---- Promotion card / composer ---- */
    promo_card_title: "Run a Promotion",
    promo_quota: "{used} of {limit} promotions used this week — resets Monday",
    promo_quota_reached: "Limit reached — resets Monday",
    promo_send_now: "Send Now",
    promo_metric_sent: "Sent",
    promo_metric_open: "Open rate",
    promo_metric_revenue: "Revenue",
    promo_metric_revenue_attr: "attributed",
    promo_metric_disclaimer: "Revenue attributed to promotions this week. Estimated — based on customer-logged spend after receiving a promotion.",
    promo_step1_title: "Choose your audience",
    promo_step2_title: "Write your message",
    promo_step3_title: "Preview",
    promo_step4_title: "Promotion sent",
    promo_step_indicator: "Step {step} of 4 · {label}",
    promo_step_label_audience: "Audience",
    promo_step_label_message: "Message",
    promo_step_label_preview: "Preview",
    promo_step_label_sent: "Sent",
    promo_aud_all: "All Customers",
    promo_aud_all_hint: "Everyone in your Customer Book",
    promo_aud_recent: "Recent Visitors",
    promo_aud_recent_hint: "Checked in last 30 days",
    promo_aud_named: "Named Customers Only",
    promo_aud_named_hint: "Excludes anonymous",
    promo_aud_specific: "Specific Customer",
    promo_aud_specific_hint: "Pick from your Customer Book",
    promo_aud_eligible: "eligible",
    promo_aud_dnc_note: "Customers with \"Do Not Contact\" enabled are always excluded.",
    promo_message_label: "Message",
    promo_message_placeholder: "We have something special tonight…",
    promo_message_too_long: "Message is too long ({len} / 160).",
    promo_no_links: "External links are not allowed. Please remove the URL from your message.",
    promo_suggestions: "Suggestions",
    promo_preview_from: "Message from {name}",
    promo_preview_sub: "This is exactly how the customer sees the notification.",
    promo_confirm_send: "Confirm Send",
    promo_edit: "Edit",
    promo_back: "Back",
    promo_cancel: "Cancel",
    promo_next_message: "Next: Write Message",
    promo_next_preview: "Next: Preview",
    promo_done: "Done",
    promo_sent_to: "Promotion sent to {count} customer",
    promo_sent_to_plural: "Promotion sent to {count} customers",
    promo_quota_used_summary: "{used} of {limit} used this week — resets Monday",

    /* ---- Customer Book ---- */
    customer_book: "Customer Book",
    cb_all: "All Customers",
    cb_starred: "Starred",
    cb_recent: "Recent",
    cb_frequent: "Frequent",
    cb_history: "Check-In History",
    cb_history_vendor: "Interaction History",
    cb_search: "Search customer…",
    cb_anon: "Anonymous Customer",
    cb_empty_title: "No customers yet",
    cb_empty_body: "Your Customer Book will fill as people visit and save your business.",
    cb_includes_anon: "Save count includes customers who have not created an account.",
    cb_sort_recent: "Most Recent",
    cb_sort_visits: "Most Visits",
    cb_sort_starred: "Starred First",
    cb_sort_spend: "Highest Spend",
    cb_saved_no_visits: "Saved · 0 visits",
    cb_last_visited: "last",
    cb_spend: "Spend:",
    cb_spend_est_short: "est. by customer, not verified",
    cb_star_label: "Star this customer",
    cb_spend_tier: "Spend tier",
    cb_spend_tier_note: "This is your private assessment and is never shown to the customer.",
    cb_tier_casual: "Casual",
    cb_tier_regular: "Regular",
    cb_tier_high_value: "High Value",
    cb_tier_vip: "VIP",
    cb_dnc: "Do Not Contact",
    cb_dnc_note: "This customer will not receive any promotions from you.",
    cb_private_notes: "Private notes",
    cb_private_notes_ph: "Your private notes about this customer — never shown to the customer or shared with other businesses.",
    cb_send_promo: "Send Promotion",
    cb_estimated_spend: "Estimated spend: ${amount}",
    cb_visits_count: "{count} visits",
    cb_saved_label: "Saved",

    /* ---- Vendor interaction language ---- */
    recent_interactions: "Recent Interactions",
    frequent_interactions: "Frequent Interactions",
    vendor_interaction_anon: "A customer just interacted with your profile",

    /* ---- Analytics ---- */
    section_this_week: "This week",
    section_performance: "Performance",
    section_audience: "Audience",
    estimated_spend: "Estimated spend",
    saves_includes_anon: "Includes customers without accounts",
    repeat_visitors: "Repeat visitors",
    avg_visits_per_customer: "Avg visits / customer",
    conversions: "Conversions",
    save_to_checkin: "Save → check-ins",
    save_to_interaction: "Save → interactions",
    checkin_to_repeat: "Check-ins → repeat",
    interaction_to_repeat: "Interactions → repeat",
    returning_visitors: "Returning visitors",
    named_vs_anon: "Named vs Anonymous",
    named_label: "named",
    anon_label: "anonymous",
    languages: "Languages",
    top_day_checkin: "Top check-in day",
    top_day_interaction: "Top interaction day",
    new_vs_returning: "New vs returning",
    pct_new: "{new}% new · {ret}% returning",
    vs_last_week: "{pct}% vs last week",
    analytics_heading: "Analytics",

    /* ---- Profile / form fields ---- */
    profile_heading_business: "Business details",
    profile_heading_vendor: "Your details",
    profile_sub_business: "Changes go live immediately across the app.",
    profile_sub_vendor: "Visible to travelers on your public vendor profile.",
    section_basics: "Basics",
    section_location: "Location",
    section_contact: "Contact",
    section_operations: "Operations",
    section_photos: "Photos",
    section_identity: "Identity",
    section_languages: "Languages",
    section_areas: "Areas you work",
    section_app_language: "App language",
    field_business_name: "Business name",
    field_category: "Category",
    field_description: "Description",
    field_description_ph: "A short paragraph for travelers.",
    field_address: "Address",
    field_neighborhood: "Neighborhood",
    field_neighborhood_ph: "Vedado · Habana Vieja …",
    field_gmaps: "Google Maps URL",
    field_lat: "Latitude",
    field_lng: "Longitude",
    field_phone: "Phone",
    field_whatsapp: "WhatsApp",
    field_website: "Website",
    field_instagram: "Instagram",
    field_facebook: "Facebook",
    field_facebook_ph: "page name or URL",
    field_hours: "Hours",
    field_hours_ph: "Mon-Sun 4pm-2am",
    field_hours_section: "HOURS",
    field_hours_closed: "Closed",
    field_hours_copy_monday: "Same as Monday",
    field_hours_legacy_note: "Legacy free-text hours",
    field_smoking_policy: "Smoking policy",
    field_full_name: "Full name",
    field_bio: "Bio",
    field_bio_ph: "A short bio that helps travelers trust you.",
    save_changes: "Save changes",
    save_profile: "Save profile",
    add_photo: "Add photo",
    uploading: "Uploading…",
    change_photo: "Change photo",
    photo_uploaded: "Photo uploaded",
    photo_removed: "Photo removed",
    delete_photo_confirm: "Delete this photo?",
    profile_saved: "Profile saved · changes are live",
    vip_enabled_label: "VIP available",
    vip_enabled_hint: "Show a VIP badge on your profile and Discover card.",

    /* ---- Restore banner ---- */
    restore_welcome_back: "Welcome back",
    restore_default_body: "Welcome back — your features have been restored.",
    restore_drafts_one: "You have {count} promotion draft waiting — review before sending.",
    restore_drafts_many: "You have {count} promotion drafts waiting — review before sending.",
    restore_ht_prompt: "Would you like to turn Here Tonight back on?",
    restore_ht_yes: "Yes — turn on Here Tonight",
    restore_ht_no: "Not right now",
    restore_hidden_line: "Your listing is now visible in search and the directory again.",

    /* ---- Header dropdown ---- */
    nav_my_dashboard: "My Dashboard",
    nav_my_profile: "My Profile",
    nav_edit_profile: "Edit Profile",
    nav_qr: "QR Code",
    nav_support: "Support",
    nav_signout: "Log Out",
    nav_language: "Language",

    /* ---- Errors / toasts ---- */
    error_generic: "Something went wrong — please try again",
    saved_toast: "Changes saved",
    field_required: "This field is required",
    invalid_email: "Please enter a valid email",
    message_empty: "Message cannot be empty",
    limit_reached_week: "You have reached your promotion limit for this week",

    /* ---- Inactivity / pause ---- */
    listing_paused_owner: "Your listing features are paused — log in to reactivate",
    listing_hidden_owner: "Your listing has been hidden from discovery — log in to restore",
    vendor_paused_owner: "Your features are paused — log in to reactivate",
    /* ---- Vendor onboarding ---- */
    ven_onb_eyebrow: "Vendor onboarding · {n} of {total}",
    ven_onb_back: "Back",
    ven_onb_next: "Next",
    ven_onb_done: "Got it",
    ven_onb_skip: "Skip — I'll review later in Profile",
    ven_onb_card1_title: "Show your QR after every interaction",
    ven_onb_card1_body: "Travelers scan it to mark a meet and unlock the ability to leave you a review. No QR scan = no review opportunity.",
    ven_onb_card2_title: "Reviews are how you grow",
    ven_onb_card2_body: "Quality interactions lead to 4-5 star reviews. Three approved reviews unlock star ratings on your public profile.",
    ven_onb_card3_title: "Reactivate every week",
    ven_onb_card3_body: "Tap the Reactivate button once every 7 days to stay visible in listings. We send a reminder 24 hours before you expire.",
    ven_onb_card4_title: "WhatsApp is your business line",
    ven_onb_card4_body: "Travelers contact you via WhatsApp directly from your profile. Keep notifications on and respond within an hour for best results.",
  },
  es: {
    tab_overview: "Resumen",
    tab_customers: "Clientes",
    tab_analytics: "Análisis",
    tab_profile: "Perfil",
    tab_my_business: "Mi Negocio",
    dashboard_label: "TABLERO",
    dashboard_business: "Negocio",
    dashboard_user: "Usuario",
    back_to_home: "Volver al Inicio",
    edit: "Editar",
    tab_jobs: "Trabajos",
    tab_messages: "Mensajes",
    jobs_heading: "Trabajos",
    messages_heading: "Mensajes",
    id_locked_label: "Foto de ID",

    welcome_back: "Bienvenido de vuelta",
    eyebrow_this_week: "Esta semana",
    todays_checkins: "Visitas de hoy",
    todays_interactions: "Interacciones de hoy",
    followers: "Seguidores",
    saves: "Guardados",
    qr_scans: "Escaneos QR",
    status: "Estado",
    status_active: "Activo",
    status_inactive: "Inactivo",
    your_qr: "Tu QR",
    your_qr_open: "Abrir",
    your_qr_sub: "Descargar y compartir",
    saves_this_week: "Guardados esta semana",
    checkins_this_week: "Visitas esta semana",
    interactions_this_week: "Interacciones esta semana",
    rev_section: "Ingresos",
    rev_week: "Ingresos estimados esta semana",
    rev_week_vendor: "Ingresos por interacción estimados esta semana",
    rev_lifetime: "Ingresos estimados de por vida",
    rev_disclaimer: "Estimado por clientes — no son datos de transacciones verificadas",
    rev_top_spender: "Mayor gasto esta semana",
    visit_singular: "visita",
    visit_plural: "visitas",
    interaction_singular: "interacción",
    interaction_plural: "interacciones",
    spend_estimated_note: "Estimado por el cliente — no son datos de transacciones verificadas",
    no_spend_recorded: "Sin gasto registrado",
    tonights_controls: "Controles de esta noche",
    open_now: "Abierto ahora",
    open_now_hint: "Mostrar a los viajeros como abierto. Apágalo en cuanto cierres.",
    here_tonight: "Aquí Esta Noche",
    here_tonight_hint: "Destaca este lugar en la sección Esta Noche de la pantalla principal.",
    vip_available: "VIP disponible",
    vip_available_hint: "Indica que hay una mesa o salón VIP disponible para reserva.",
    open_book: "Abre tu Libro de Clientes completo.",
    qr_show_door: "Muéstralo en la entrada para registrar visitas.",
    profile_card_hint: "Horario, fotos, contacto, redes sociales.",
    quick_actions: "Acciones rápidas",
    send_promotion: "Enviar una promoción",
    send_promotion_hint: "Llega a los clientes que te guardaron o interactuaron contigo.",
    view_customer_book: "Ver Libro de Clientes",
    view_customer_book_hint: "Clientes destacados, notas privadas, historial de interacciones.",
    recent_interactions_label: "Interacciones recientes",
    recent_interactions_empty: "Aún no hay interacciones — tus escaneos QR y contactos de WhatsApp aparecerán aquí.",
    reactivate_listing: "Reactivar tu negocio",
    reactivate_profile: "Reactivar tu perfil",
    listing_paused_user: "Es posible que este negocio ya no esté activo — contáctalos directamente para confirmar",

    promo_card_title: "Crear una Promoción",
    promo_quota: "{used} de {limit} promociones usadas esta semana — se reinicia el lunes",
    promo_quota_reached: "Límite alcanzado — se reinicia el lunes",
    promo_send_now: "Enviar Ahora",
    promo_metric_sent: "Enviadas",
    promo_metric_open: "Tasa de apertura",
    promo_metric_revenue: "Ingresos",
    promo_metric_revenue_attr: "atribuidos",
    promo_metric_disclaimer: "Ingresos atribuidos a promociones esta semana. Estimado — basado en el gasto registrado por clientes después de recibir una promoción.",
    promo_step1_title: "Elige tu audiencia",
    promo_step2_title: "Escribe tu mensaje",
    promo_step3_title: "Vista previa",
    promo_step4_title: "Promoción enviada",
    promo_step_indicator: "Paso {step} de 4 · {label}",
    promo_step_label_audience: "Audiencia",
    promo_step_label_message: "Mensaje",
    promo_step_label_preview: "Vista previa",
    promo_step_label_sent: "Enviada",
    promo_aud_all: "Todos los Clientes",
    promo_aud_all_hint: "Todos los que están en tu Libro de Clientes",
    promo_aud_recent: "Visitantes Recientes",
    promo_aud_recent_hint: "Visitaron en los últimos 30 días",
    promo_aud_named: "Solo Clientes Nombrados",
    promo_aud_named_hint: "Excluye anónimos",
    promo_aud_specific: "Cliente Específico",
    promo_aud_specific_hint: "Elige de tu Libro de Clientes",
    promo_aud_eligible: "elegibles",
    promo_aud_dnc_note: "Los clientes con \"No Contactar\" activado siempre se excluyen.",
    promo_message_label: "Mensaje",
    promo_message_placeholder: "Tenemos algo especial esta noche…",
    promo_message_too_long: "El mensaje es demasiado largo ({len} / 160).",
    promo_no_links: "No se permiten enlaces externos. Por favor elimina la URL de tu mensaje.",
    promo_suggestions: "Sugerencias",
    promo_preview_from: "Mensaje de {name}",
    promo_preview_sub: "Así verá el cliente exactamente la notificación.",
    promo_confirm_send: "Confirmar envío",
    promo_edit: "Editar",
    promo_back: "Atrás",
    promo_cancel: "Cancelar",
    promo_next_message: "Siguiente: Escribir Mensaje",
    promo_next_preview: "Siguiente: Vista previa",
    promo_done: "Listo",
    promo_sent_to: "Promoción enviada a {count} cliente",
    promo_sent_to_plural: "Promoción enviada a {count} clientes",
    promo_quota_used_summary: "{used} de {limit} usadas esta semana — se reinicia el lunes",

    customer_book: "Libro de Clientes",
    cb_all: "Todos los Clientes",
    cb_starred: "Destacados",
    cb_recent: "Recientes",
    cb_frequent: "Frecuentes",
    cb_history: "Historial de Visitas",
    cb_history_vendor: "Historial de Interacciones",
    cb_search: "Buscar cliente…",
    cb_anon: "Cliente Anónimo",
    cb_empty_title: "Aún no hay clientes",
    cb_empty_body: "Tu Libro de Clientes se llenará a medida que la gente visite y guarde tu negocio.",
    cb_includes_anon: "El conteo de guardados incluye clientes que no han creado una cuenta.",
    cb_sort_recent: "Más Reciente",
    cb_sort_visits: "Más Visitas",
    cb_sort_starred: "Destacados Primero",
    cb_sort_spend: "Mayor Gasto",
    cb_saved_no_visits: "Guardado · 0 visitas",
    cb_last_visited: "última",
    cb_spend: "Gasto:",
    cb_spend_est_short: "est. por el cliente, no verificado",
    cb_star_label: "Destacar este cliente",
    cb_spend_tier: "Nivel de gasto",
    cb_spend_tier_note: "Esta es tu evaluación privada y nunca se muestra al cliente.",
    cb_tier_casual: "Casual",
    cb_tier_regular: "Regular",
    cb_tier_high_value: "Alto Valor",
    cb_tier_vip: "VIP",
    cb_dnc: "No Contactar",
    cb_dnc_note: "Este cliente no recibirá ninguna promoción tuya.",
    cb_private_notes: "Notas privadas",
    cb_private_notes_ph: "Tus notas privadas sobre este cliente — nunca se muestran al cliente ni se comparten con otros negocios.",
    cb_send_promo: "Enviar Promoción",
    cb_estimated_spend: "Gasto estimado: ${amount}",
    cb_visits_count: "{count} visitas",
    cb_saved_label: "Guardado",

    recent_interactions: "Interacciones Recientes",
    frequent_interactions: "Interacciones Frecuentes",
    vendor_interaction_anon: "Un cliente acaba de interactuar con tu perfil",

    section_this_week: "Esta semana",
    section_performance: "Rendimiento",
    section_audience: "Audiencia",
    estimated_spend: "Gasto estimado",
    saves_includes_anon: "Incluye clientes sin cuenta",
    repeat_visitors: "Visitantes recurrentes",
    avg_visits_per_customer: "Promedio visitas / cliente",
    conversions: "Conversiones",
    save_to_checkin: "Guardado → visitas",
    save_to_interaction: "Guardado → interacciones",
    checkin_to_repeat: "Visitas → recurrentes",
    interaction_to_repeat: "Interacciones → recurrentes",
    returning_visitors: "Visitantes recurrentes",
    named_vs_anon: "Nombrado vs Anónimo",
    named_label: "nombrado",
    anon_label: "anónimo",
    languages: "Idiomas",
    top_day_checkin: "Día con más visitas",
    top_day_interaction: "Día con más interacciones",
    new_vs_returning: "Nuevo vs recurrente",
    pct_new: "{new}% nuevos · {ret}% recurrentes",
    vs_last_week: "{pct}% vs semana pasada",
    analytics_heading: "Análisis",

    profile_heading_business: "Detalles del negocio",
    profile_heading_vendor: "Tus datos",
    profile_sub_business: "Los cambios se aplican al instante en toda la app.",
    profile_sub_vendor: "Visible para viajeros en tu perfil público de vendedor.",
    section_basics: "Básico",
    section_location: "Ubicación",
    section_contact: "Contacto",
    section_operations: "Operaciones",
    section_photos: "Fotos",
    section_identity: "Identidad",
    section_languages: "Idiomas",
    section_areas: "Zonas donde trabajas",
    section_app_language: "Idioma de la app",
    field_business_name: "Nombre del negocio",
    field_category: "Categoría",
    field_description: "Descripción",
    field_description_ph: "Un breve párrafo para viajeros.",
    field_address: "Dirección",
    field_neighborhood: "Barrio",
    field_neighborhood_ph: "Vedado · Habana Vieja …",
    field_gmaps: "URL de Google Maps",
    field_lat: "Latitud",
    field_lng: "Longitud",
    field_phone: "Teléfono",
    field_whatsapp: "WhatsApp",
    field_website: "Sitio web",
    field_instagram: "Instagram",
    field_facebook: "Facebook",
    field_facebook_ph: "nombre de página o URL",
    field_hours: "Horario",
    field_hours_ph: "Lun-Dom 4pm-2am",
    field_hours_section: "HORARIO",
    field_hours_closed: "Cerrado",
    field_hours_copy_monday: "Igual que el Lunes",
    field_hours_legacy_note: "Horario en texto libre (antiguo)",
    field_smoking_policy: "Política de fumadores",
    field_full_name: "Nombre completo",
    field_bio: "Biografía",
    field_bio_ph: "Una breve biografía que ayude a los viajeros a confiar en ti.",
    save_changes: "Guardar cambios",
    save_profile: "Guardar perfil",
    add_photo: "Añadir foto",
    uploading: "Subiendo…",
    change_photo: "Cambiar foto",
    photo_uploaded: "Foto subida",
    photo_removed: "Foto eliminada",
    delete_photo_confirm: "¿Eliminar esta foto?",
    profile_saved: "Perfil guardado · los cambios están en vivo",
    vip_enabled_label: "VIP disponible",
    vip_enabled_hint: "Muestra una insignia VIP en tu perfil y tarjeta de Descubrir.",

    restore_welcome_back: "Bienvenido de vuelta",
    restore_default_body: "Bienvenido de vuelta — tus funciones han sido restauradas.",
    restore_drafts_one: "Tienes {count} borrador de promoción esperando — revisa antes de enviar.",
    restore_drafts_many: "Tienes {count} borradores de promoción esperando — revisa antes de enviar.",
    restore_ht_prompt: "¿Deseas activar Aquí Esta Noche nuevamente?",
    restore_ht_yes: "Sí — activar Aquí Esta Noche",
    restore_ht_no: "Ahora no",
    restore_hidden_line: "Tu negocio es visible nuevamente en la búsqueda y el directorio.",

    nav_my_dashboard: "Mi Panel",
    nav_my_profile: "Mi Perfil",
    nav_edit_profile: "Editar Perfil",
    nav_qr: "Código QR",
    nav_support: "Soporte",
    nav_signout: "Cerrar Sesión",
    nav_language: "Idioma",

    error_generic: "Algo salió mal — por favor intenta de nuevo",
    saved_toast: "Cambios guardados",
    field_required: "Este campo es requerido",
    invalid_email: "Por favor ingresa un correo válido",
    message_empty: "El mensaje no puede estar vacío",
    limit_reached_week: "Has alcanzado tu límite de promociones esta semana",

    listing_paused_owner: "Las funciones de tu negocio están pausadas — inicia sesión para reactivar",
    listing_hidden_owner: "Tu negocio ha sido ocultado de los resultados — inicia sesión para restaurar",
    vendor_paused_owner: "Tus funciones están pausadas — inicia sesión para reactivar",
    /* ---- Vendor onboarding ---- */
    ven_onb_eyebrow: "Bienvenida del vendedor · {n} de {total}",
    ven_onb_back: "Atrás",
    ven_onb_next: "Siguiente",
    ven_onb_done: "Entendido",
    ven_onb_skip: "Omitir — lo revisaré luego en Perfil",
    ven_onb_card1_title: "Muestra tu QR después de cada interacción",
    ven_onb_card1_body: "Los viajeros lo escanean para registrar el encuentro y desbloquear la opción de dejarte una reseña. Sin escaneo de QR = sin reseña.",
    ven_onb_card2_title: "Las reseñas son tu manera de crecer",
    ven_onb_card2_body: "Interacciones de calidad generan reseñas de 4-5 estrellas. Tres reseñas aprobadas activan las estrellas en tu perfil público.",
    ven_onb_card3_title: "Reactívate cada semana",
    ven_onb_card3_body: "Toca el botón Reactivar una vez cada 7 días para mantenerte visible en los listados. Te avisamos 24 horas antes de que expire.",
    ven_onb_card4_title: "WhatsApp es tu línea de negocio",
    ven_onb_card4_body: "Los viajeros te contactan por WhatsApp directamente desde tu perfil. Mantén las notificaciones activadas y responde en una hora para mejores resultados.",
  },
};

const LS_KEY = "habano.dashboard_lang";

/**
 * useDashboardI18n
 *
 * iter210 — Formerly kept its own `useState` + separate `habano.dashboard_lang`
 * localStorage key, which meant flipping the global app language never
 * touched the dashboards. It now consumes the **global** `useI18n()` context
 * as the single source of truth, so a language switch anywhere in the app
 * updates the vendor + business dashboards instantly. The dashboard-scoped
 * `setLang` delegates to the global setter and additionally persists the
 * choice to the server (`PATCH /{kind}/me/language`).
 */
export function useDashboardI18n(kind /* "business" | "vendor" */) {
  const { lang, setLang: setGlobalLang } = useI18n();

  // Hydrate from the vendor/business profile on mount — only override the
  // global choice if the user has never set a localStorage value.
  useEffect(() => {
    const path = kind === "vendor" ? "/vendor/me" : "/business/me";
    api.get(path).then(({ data }) => {
      if (data && (data.language === "en" || data.language === "es")) {
        let stored = null;
        try { stored = localStorage.getItem("habano.lang"); } catch { /* no-op */ }
        if (!stored) setGlobalLang(data.language);
      }
    }).catch(() => { /* keep global default */ });
  }, [kind, setGlobalLang]);

  const setLang = useCallback(async (next) => {
    if (next !== "en" && next !== "es") return;
    // Update the global i18n context so every page (nav, footer, other tabs)
    // repaints immediately.
    setGlobalLang(next);
    // Mirror to the legacy dashboard-only key so any older code that still
    // reads it stays in sync.
    try { localStorage.setItem(LS_KEY, next); } catch { /* no-op */ }
    const url = kind === "vendor" ? "/vendor/me/language" : "/business/me/language";
    try { await api.patch(url, { language: next }); } catch { /* offline-tolerant */ }
  }, [kind, setGlobalLang]);

  const t = useCallback((key, fmt) => {
    const bank = DASHBOARD_STRINGS[lang] || DASHBOARD_STRINGS.es;
    let s = bank[key] ?? DASHBOARD_STRINGS.en[key] ?? key;
    if (fmt && typeof s === "string") {
      for (const [k, v] of Object.entries(fmt)) {
        s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      }
    }
    return s;
  }, [lang]);

  return { lang, setLang, t };
}
